// AI Campaign Service — Frontend calls to backend Cloud Functions
// All AI processing and CC API calls happen server-side (secure)
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

// ============================================================
// AI CONTENT GENERATION
// ============================================================

// Get available campaign types
export const getCampaignTypes = async () => {
  const fn = httpsCallable(functions, 'getCampaignTypes');
  const result = await fn();
  return result.data;
};

// Generate AI-powered campaign content
// campaignType: 'weekly_digest' | 'event_promo' | 'new_jobs_alert' | 're_engagement' | 'welcome_drip' | 'custom'
export const generateCampaignContent = async (campaignType, options = {}) => {
  const fn = httpsCallable(functions, 'generateCampaignContent');
  const result = await fn({
    campaignType,
    customPrompt: options.customPrompt || null,
    targetEventId: options.targetEventId || null,
  });
  return result.data;
};

// Generate social media posts
export const generateSocialPosts = async (topic, options = {}) => {
  const fn = httpsCallable(functions, 'generateSocialPosts');
  const result = await fn({
    topic,
    eventId: options.eventId || null,
    platform: options.platform || null,
  });
  return result.data;
};

// ============================================================
// CONSTANT CONTACT (via backend)
// ============================================================

// Sync member to Constant Contact
export const syncMemberToCC = async (memberData) => {
  const fn = httpsCallable(functions, 'syncMemberToCC');
  const result = await fn(memberData);
  return result.data;
};

// Subscribe to newsletter
export const subscribeToNewsletter = async (email, firstName = '', lastName = '') => {
  const fn = httpsCallable(functions, 'subscribeNewsletter');
  const result = await fn({ email, firstName, lastName });
  return result.data;
};

// Import CSV contacts (admin only)
export const importCSVContacts = async (contacts, listId = null) => {
  const fn = httpsCallable(functions, 'importCSVContacts');
  const result = await fn({ contacts, listId });
  return result.data;
};

// Create and send campaign through Constant Contact (admin only)
export const createAndSendCampaign = async (campaignData) => {
  const fn = httpsCallable(functions, 'createCampaign');
  const result = await fn(campaignData);
  return result.data;
};

// Get contact lists from Constant Contact
export const getContactLists = async () => {
  const fn = httpsCallable(functions, 'getCCContactLists');
  const result = await fn();
  return result.data;
};

// Get campaign history/stats
export const getCampaignStats = async () => {
  const fn = httpsCallable(functions, 'getCampaignStats');
  const result = await fn();
  return result.data;
};

// ============================================================
// FULL WORKFLOW: Generate + Send
// ============================================================

// Generate content with AI, then create campaign in CC, ready to send
export const generateAndPrepareCampaign = async (campaignType, options = {}) => {
  // Step 1: Generate content with Claude
  const aiResult = await generateCampaignContent(campaignType, options);
  
  if (!aiResult.success) {
    throw new Error('Failed to generate content');
  }

  // Return the generated content for admin review before sending
  return {
    content: aiResult.content,
    campaignType,
    // Admin can review, edit, then call createAndSendCampaign() to push to CC
  };
};

// Parse CSV file to contact objects
export const parseCSVFile = (csvText) => {
  const lines = csvText.trim().split('\n');
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  
  return lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const contact = {};
    
    headers.forEach((header, i) => {
      const value = values[i] || '';
      // Map common CSV headers to our contact format
      if (header.includes('email')) contact.email = value;
      else if (header.includes('first') && header.includes('name')) contact.firstName = value;
      else if (header.includes('last') && header.includes('name')) contact.lastName = value;
      else if (header === 'name' && !contact.firstName) {
        const parts = value.split(' ');
        contact.firstName = parts[0];
        contact.lastName = parts.slice(1).join(' ');
      }
      else if (header.includes('company') || header.includes('organization')) contact.company = value;
      else if (header.includes('title') || header.includes('position')) contact.jobTitle = value;
      else if (header.includes('phone')) contact.phone = value;
    });
    
    return contact;
  }).filter(c => c.email); // Only return contacts with valid emails
};

export default {
  getCampaignTypes,
  generateCampaignContent,
  generateSocialPosts,
  syncMemberToCC,
  subscribeToNewsletter,
  importCSVContacts,
  createAndSendCampaign,
  getContactLists,
  getCampaignStats,
  generateAndPrepareCampaign,
  parseCSVFile,
};

